import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import { categories } from "@/mocks/categories";
import { getComicsByCategory, type Comic } from "@/mocks/comics";

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function CategoryPage() {
  const { slug = "all" } = useParams<{ slug: string }>();
  const [comicList, setComicList] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<"popular" | "latest" | "rating">("popular");

  useEffect(() => {
    let list = getComicsByCategory(slug);
    if (sortBy === "latest") {
      list = [...list].sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    } else if (sortBy === "rating") {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => b.views - a.views);
    }
    setComicList(list);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, sortBy]);

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${getCategoryName(slug)} - 韩国漫画分类`,
    description: `韩漫在线${getCategoryName(slug)}分类，免费在线阅读高清韩国漫画，下拉式流畅阅读体验。`,
    url: typeof window !== "undefined" ? window.location.href : "",
    isPartOf: {
      "@type": "WebSite",
      name: "韩漫在线",
      url: typeof window !== "undefined" ? window.location.origin : "",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: typeof window !== "undefined" ? window.location.origin + "/" : "" },
        { "@type": "ListItem", position: 2, name: "漫画分类", item: typeof window !== "undefined" ? window.location.origin + "/category/all" : "" },
        { "@type": "ListItem", position: 3, name: getCategoryName(slug) },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-100 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category/all" className="hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900">{getCategoryName(slug)}</span>
            </div>
          </div>
        </nav>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {slug === "all" ? "全部漫画" : getCategoryName(slug)}
              </h1>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                {[
                  { key: "popular", label: "最受欢迎" },
                  { key: "latest", label: "最新更新" },
                  { key: "rating", label: "评分最高" },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key as typeof sortBy)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === s.key
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {comicList.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {comicList.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[5/7]">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full">
                          {comic.rating}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <span className="text-xs text-white/90">{comic.status}</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{comic.author}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}